import { formatValue } from "../utils/helpers";
import metricMetadataIcon from '../assets/metric-metadata-icon-slate.svg';
import { CSSProperties, useContext, useEffect, useRef, useState } from "react";
import MetricMetadataModal from "./MetricMetadataModal";
import { DisaggregatedMetric, DistributionData, GeoDetails, GeoTag, RawIndicatorData } from "../services/types";
import MetadataContext from "../context/MetadataContext";
import { fetchDistributionData, fetchIndicatorBinCount, indexMetricIds } from "../utils/dashboardUtils";
import { useCountyFips } from "../context/CountyFipsContext";
// import { getIconByName } from "../utils/icons";
import { useDashboardColor } from "../context/DashboardColorContext";
import DefaultIcon from '../assets/general/default-metric.svg?react'
import { trackEvent } from "../analytics/utils/analytics";
import { skipHistogram } from "../utils/chartUtils";

interface MetricCardProps {
    metric: DisaggregatedMetric;
    width: string;
    disaggData: DisaggregatedMetric[] | null;
    geoDetails: GeoDetails[];
    geoTag: GeoTag | null;
    selectionActive: boolean;
    svg?: React.FC<React.SVGProps<SVGSVGElement>>;
    showMetadataIcon?: boolean;
};

const MetricCard = ({ metric, disaggData, geoDetails, geoTag, selectionActive, svg: SvgIcon, showMetadataIcon = true }: MetricCardProps) => {
    const { metadata: allMetadata, loading: metadataLoading } =
    useContext(MetadataContext);
    const { countyFips } = useCountyFips();
    const color = useDashboardColor();
    const IconComponent = SvgIcon || DefaultIcon;

    const isIndexMetric = indexMetricIds.includes(metric.id);

    const [showMetadata, setShowMetadata] = useState(false);
    const [distributionData, setDistributionData] = useState<RawIndicatorData[] | null>(null);
    const [countyDistributionData, setCountyDistributionData] = useState<DistributionData[] | null>(null);

    const [distributionLoading, setDistributionLoading] = useState(false);
    const [distributionError, setDistributionError] = useState<Error | null>(null);

    // Destructure metric properties
    const { id, name, versions } = metric;
    const { value, unit, unit_descriptor, percent_calc } = versions[0];

    const metricMetadata = allMetadata?.find((item) => item.id === id);
    const metadata = metricMetadata ? [metricMetadata] : null;

    const val = unit === "ratio" ? `${percent_calc?.n}/${percent_calc?.d}` : value;

    // Refs for metadata modal
    const triggerRef = useRef<HTMLButtonElement | null>(null);
    const modalId = `metric-metadata-modal-${id ?? 'main'}`;
    const clickDebounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);

    const fetchCache = useRef<Record<string, Promise<any> | undefined>>({});

    function fetchWithCache<T>(key: string, fetchFn: () => Promise<T>): Promise<T> {
        if (fetchCache.current[key]) {
            // Return ongoing request if already in-flight
            return fetchCache.current[key] as Promise<T>;
        }

        const promise = fetchFn().finally(() => {
            delete fetchCache.current[key];
        });

        fetchCache.current[key] = promise;
        return promise;
    }

    useEffect(() => {
        if (!showMetadata) return;
        if (skipHistogram.includes(id)) return;

        const loadDistributionData = async () => {
            setDistributionLoading(true);
            setDistributionError(null);

            try {
                const requests: Promise<void>[] = [];

                // Selected-area distribution
                if (!distributionData && selectionActive) {
                    const distKey =
                        `distData-${id}-` +
                        `${geoDetails.map(d => d.lod).join(",")}-` +
                        `${geoTag?.type}-${geoTag?.value}`;

                    requests.push(
                        fetchWithCache(
                            distKey,
                            () => fetchIndicatorBinCount(id, geoDetails, geoTag)
                        ).then(res => {
                            if (res) {
                                setDistributionData(res);
                            }
                        })
                    );
                }

                // County comparison distribution
                if (!countyDistributionData && countyFips) {
                    const countyKey =
                        `countyDist-${id}-${countyFips.value}`;

                    requests.push(
                        fetchWithCache(
                            countyKey,
                            () => fetchDistributionData(id, [], countyFips)
                        ).then(res => {
                            if (res) {
                                setCountyDistributionData(res);
                            }
                        })
                    );
                }

                await Promise.allSettled(requests);
            } catch (error) {
                console.error("Error loading distribution data:", error);

                setDistributionError(
                    error instanceof Error
                        ? error
                        : new Error("Failed to load distribution data")
                );
            } finally {
                setDistributionLoading(false);
            }
        };

        loadDistributionData();
    }, [
        showMetadata,
        id,
        geoDetails,
        geoTag,
        countyFips,
        selectionActive
    ]);

    // Track metric views in GA
    useEffect(() => {
        trackEvent("metric_view", {
            metric_id: id,
            metric_name: name,
            geo: geoTag?.value ?? geoDetails,
        })
    }, [id]);

    const handleClick = () => {
        if (clickDebounceRef.current) {
            clearTimeout(clickDebounceRef.current);
        }

        clickDebounceRef.current = setTimeout(() => {
            setShowMetadata(prev => !prev);
        }, 150);
    };

    return (
        <div 
            className="relative overflow-hidden group hover:shadow-lg transition-all border rounded-xl bg-white" 
            style={{ 
                borderLeftWidth: isIndexMetric ? "4px" : undefined, borderLeftColor: isIndexMetric ? color : undefined,
                }}
            onClick={handleClick}
            role="button"
            tabIndex={0}
            onKeyDown={(e) => {
                if (e.key === "Enter" || e.key === " ") {
                    e.preventDefault();
                    handleClick();
                }
            }}
        >
            <div className="p-4">
                <div className="flex items-start justify-between mb-1">
                    <div className="flex items-center space-x-2">
                        {/* Metric Icon (dynamic colors) */}
                        <div
                            className="group rounded-lg p-2 transition-colors bg-[var(--bg)] group-hover:bg-[var(--bg-hover)]"
                            style={
                                {
                                "--bg": `${color}20`,
                                "--bg-hover": `${color}40`,
                                } as CSSProperties
                            }
                        >
                            {SvgIcon && (
                                <IconComponent
                                    className="w-8 h-8"
                                    style={{ color }}
                                    aria-label={`${name} icon`}
                                    role="img"
                                />
                            )}
                        </div>
                        {/* Metric Value */}
                        <span className="text-3xl font-semibold tracking-tight">
                            {formatValue(val, unit, id)}
                        </span>
                    </div>

                    {/* Metadata Icon */}
                    {showMetadataIcon && 
                        <button
                            type="button"
                            onClick={(e) => {
                                e.stopPropagation();
                                handleClick();
                            }}
                            aria-haspopup="dialog"
                            aria-expanded={showMetadata ? 'true' : 'false'}
                            aria-controls={modalId}
                            aria-label={`Additional information about ${name}`}
                            className="
                            inline-flex h-6 w-6 items-center justify-center rounded
                            transition-transform hover:scale-105 focus:scale-105
                            focus-visible:outline-none
                            focus-visible:ring-2 focus-visible:ring-offset-2
                            focus-visible:ring-neutral-900 focus-visible:ring-offset-white
                            dark:focus-visible:ring-white dark:focus-visible:ring-offset-neutral-900
                            "
                        >
                            <img src={metricMetadataIcon} alt="" aria-hidden="true" className="h-3 w-3" draggable="false" />
                        </button>
                    }
                </div>

                <div className="space-y-0.5">
                    <h3 className="text-sm font-semibold text-port-text">
                        {name}
                    </h3>
                    <p className="text-xs text-muted-foreground leading-tight">
                        {unit_descriptor}
                    </p>
                    {isIndexMetric &&
                        <div className="rounded-lg h-4 w-10 px-1.5 text-[10px] text-white text-center" style={{ backgroundColor: color }}>Index</div> 
                    }
                </div>
            </div>
            <div className="absolute bottom-0 left-0 right-0 h-1 bg-gradient-to-r from-[#2258e5]/50 to-[#00ac4e]/50 opacity-0 group-hover:opacity-100 transition-opacity" />

            <MetricMetadataModal
                isOpen={showMetadata}
                onClose={handleClick}
                id={id}
                metadata={metadata}
                stateData={null}
                nationalData={null}
                disaggData={disaggData}
                distributionData={distributionData}
                countyDistributionData={countyDistributionData}
                selectionActive={selectionActive}
                geoTag={geoTag}
                // svg={svg}
                loading={metadataLoading}
                distributionLoading={distributionLoading}
                error={distributionError}
                triggerRef={triggerRef}
                modalId={modalId}
            />
        </div>
    );
};


export default MetricCard;
